import React, { useState, useEffect } from 'react';
import { Plus, Minus, Trash2, Save, Calendar, Package, IndianRupee, ShoppingBag, AlertCircle, CheckCircle } from 'lucide-react';
import { formatCurrency } from '../Helpers';
import SearchableSelect from './SearchableSelect';

const BulkSalesEntry = ({ inventory, onFinalizeBulkSales }) => {
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [salesEntries, setSalesEntries] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');

    // Prepare inventory options for SearchableSelect
    const inventoryOptions = inventory.map(item => ({
        label: `${item.itemName} (Stock: ${item.quantity})`,
        value: item.id,
        subtitle: formatCurrency(item.sellingPrice),
        item: item
    }));

    // Initialize with one empty entry
    useEffect(() => {
        if (salesEntries.length === 0) {
            addNewEntry();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addNewEntry = () => {
        const newEntry = {
            id: Date.now(),
            itemId: '',
            quantity: '',
            notes: ''
        };
        setSalesEntries([...salesEntries, newEntry]);
    };

    const removeEntry = (entryId) => {
        if (salesEntries.length > 1) {
            setSalesEntries(salesEntries.filter(entry => entry.id !== entryId));
        }
    };

    const updateEntry = (entryId, field, value) => {
        console.log('BulkSalesEntry: Updating entry', entryId, field, value);
        setSalesEntries(salesEntries.map(entry =>
            entry.id === entryId ? { ...entry, [field]: value } : entry
        ));
    };

    const incrementQuantity = (entryId) => {
        const entry = salesEntries.find(e => e.id === entryId);
        if (entry) {
            const currentQuantity = parseInt(entry.quantity, 10) || 0;
            const newQuantity = currentQuantity + 1;
            updateEntry(entryId, 'quantity', newQuantity.toString());
        }
    };

    const decrementQuantity = (entryId) => {
        const entry = salesEntries.find(e => e.id === entryId);
        if (entry) {
            const currentQuantity = parseInt(entry.quantity, 10) || 0;
            if (currentQuantity > 1) {
                const newQuantity = currentQuantity - 1;
                updateEntry(entryId, 'quantity', newQuantity.toString());
            }
        }
    };

    const getItemDetails = (itemId) => {
        return inventory.find(item => item.id === itemId);
    };

    const calculateTotal = () => {
        return salesEntries.reduce((total, entry) => {
            const quantity = parseFloat(entry.quantity) || 0;
            const item = getItemDetails(entry.itemId);
            const price = item ? item.sellingPrice : 0;
            return total + (quantity * price);
        }, 0);
    };

    const validateEntries = () => {
        const validEntries = salesEntries.filter(entry =>
            entry.itemId && entry.quantity
        );

        if (validEntries.length === 0) {
            setMessage('Please add at least one valid sales entry');
            setMessageType('error');
            return false;
        }

        // Check if quantities are available in inventory
        for (const entry of validEntries) {
            const item = getItemDetails(entry.itemId);
            const requestedQty = parseFloat(entry.quantity);

            if (item && requestedQty > item.quantity) {
                setMessage(`Insufficient stock for ${item.itemName}. Available: ${item.quantity}, Requested: ${requestedQty}`);
                setMessageType('error');
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateEntries()) return;

        setIsLoading(true);
        setMessage('');

        try {
            const validEntries = salesEntries.filter(entry =>
                entry.itemId && entry.quantity
            );

            const bulkSaleData = {
                date: selectedDate,
                entries: validEntries.map(entry => {
                    const item = getItemDetails(entry.itemId);
                    return {
                        itemId: entry.itemId,
                        itemName: item.itemName,
                        quantity: parseFloat(entry.quantity),
                        sellingPrice: item.sellingPrice,
                        purchasePrice: item.purchasePrice || 0,
                        notes: entry.notes
                    };
                }),
                totalAmount: calculateTotal(),
                isBulkEntry: true
            };

            await onFinalizeBulkSales(bulkSaleData);

            setMessage('Bulk sales entry saved successfully!');
            setMessageType('success');

            // Reset form
            setSalesEntries([]);
            addNewEntry();

        } catch (error) {
            setMessage(error.message || 'Failed to save bulk sales entry');
            setMessageType('error');
        } finally {
            setIsLoading(false);
        }
    };

    const dismissMessage = () => {
        setMessage('');
        setMessageType('');
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-4 sm:p-8 text-white">
                <div className="flex items-center space-x-4">
                    <div className="bg-white bg-opacity-20 rounded-full p-3 sm:p-4">
                        <ShoppingBag className="h-8 w-8 sm:h-12 sm:w-12" />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold">Bulk Sales Entry</h1>
                        <p className="text-green-100 mt-1 text-sm sm:text-base">Enter aggregate sales for a specific date</p>
                    </div>
                </div>
            </div>

            {/* Message Alert */}
            {message && (
                <div className={`relative p-4 rounded-lg border ${messageType === 'success'
                    ? 'bg-green-50 border-green-200 text-green-800'
                    : 'bg-red-50 border-red-200 text-red-800'
                    }`}>
                    <div className="flex items-start">
                        {messageType === 'success' ? (
                            <CheckCircle className="h-5 w-5 mt-0.5 mr-3 flex-shrink-0" />
                        ) : (
                            <AlertCircle className="h-5 w-5 mt-0.5 mr-3 flex-shrink-0" />
                        )}
                        <p className="flex-1">{message}</p>
                        <button
                            onClick={dismissMessage}
                            className="ml-3 flex-shrink-0 text-gray-400 hover:text-gray-600"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            {/* Date Selection */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                <div className="flex items-center space-x-3 mb-4">
                    <Calendar className="h-5 w-5 text-green-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Sales Date</h2>
                </div>
                <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
            </div>

            {/* Sales Entries */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                        <Package className="h-5 w-5 text-green-600" />
                        <h2 className="text-lg font-semibold text-gray-900">Sales Items</h2>
                    </div>
                    <button
                        onClick={addNewEntry}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                        <Plus className="h-4 w-4" />
                        <span className="hidden sm:inline">Add Item</span>
                    </button>
                </div>

                <div className="space-y-4">
                    {salesEntries.map((entry, index) => (
                        <div key={entry.id} className="border border-gray-200 rounded-lg p-4">
                            {/* Mobile Layout */}
                            <div className="sm:hidden space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">Item #{index + 1}</span>
                                    {salesEntries.length > 1 && (
                                        <button
                                            onClick={() => removeEntry(entry.id)}
                                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                                    <SearchableSelect
                                        options={inventoryOptions}
                                        value={entry.itemId}
                                        onChange={(value) => {
                                            updateEntry(entry.id, 'itemId', value);
                                        }}
                                        placeholder="Search and select product..."
                                        className="w-full"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                                    <div className="items-center space-x-2 flex justify-center">
                                        <button
                                            type="button"
                                            onClick={() => decrementQuantity(entry.id)}
                                            className="p-2 text-gray-600 hover:bg-gray-200 rounded-full transition-colors touch-target"
                                            disabled={!entry.quantity || parseInt(entry.quantity, 10) <= 1}
                                        >
                                            <Minus size={16} />
                                        </button>
                                        <input
                                            type="number"
                                            value={entry.quantity}
                                            onChange={(e) => updateEntry(entry.id, 'quantity', e.target.value)}
                                            placeholder="1"
                                            min="1"
                                            step="1"
                                            className="flex-1 max-w-20 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 text-center"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => incrementQuantity(entry.id)}
                                            className="p-2 text-gray-600 hover:bg-gray-200 rounded-full transition-colors touch-target"
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </div>
                                </div>

                                {entry.itemId && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-blue-700">Price per unit:</span>
                                            <span className="font-semibold text-blue-800">
                                                {formatCurrency(getItemDetails(entry.itemId)?.sellingPrice || 0)}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                                    <textarea
                                        value={entry.notes}
                                        onChange={(e) => updateEntry(entry.id, 'notes', e.target.value)}
                                        placeholder="Additional notes..."
                                        rows="2"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    />
                                </div>

                                {entry.quantity && entry.itemId && (
                                    <div className="bg-gray-50 rounded-lg p-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">Line Total:</span>
                                            <span className="font-semibold text-green-600">
                                                {formatCurrency(parseFloat(entry.quantity) * (getItemDetails(entry.itemId)?.sellingPrice || 0))}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Desktop Layout */}
                            <div className="hidden sm:grid sm:grid-cols-12 sm:gap-4 sm:items-start">
                                <div className="col-span-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                                    <SearchableSelect
                                        options={inventoryOptions}
                                        value={entry.itemId}
                                        onChange={(value) => {
                                            updateEntry(entry.id, 'itemId', value);
                                        }}
                                        placeholder="Search and select product..."
                                        className="w-full"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                                    <div className="flex items-center space-x-1">
                                        <button
                                            type="button"
                                            onClick={() => decrementQuantity(entry.id)}
                                            className="p-2 text-gray-600 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                                            disabled={!entry.quantity || parseInt(entry.quantity, 10) <= 1}
                                        >
                                            <Minus size={14} />
                                        </button>
                                        <input
                                            type="number"
                                            value={entry.quantity}
                                            onChange={(e) => updateEntry(entry.id, 'quantity', e.target.value)}
                                            placeholder="1"
                                            min="1"
                                            step="1"
                                            className="w-full px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 text-center"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => incrementQuantity(entry.id)}
                                            className="p-2 text-gray-600 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                                        >
                                            <Plus size={14} />
                                        </button>
                                    </div>
                                </div>

                                <div className="col-span-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                    <input
                                        type="text"
                                        value={entry.notes}
                                        onChange={(e) => updateEntry(entry.id, 'notes', e.target.value)}
                                        placeholder="Optional notes"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <div className="flex flex-col items-center justify-end h-full">
                                        {entry.itemId && (
                                            <div className="text-xs text-gray-500 mb-1">
                                                {formatCurrency(getItemDetails(entry.itemId)?.sellingPrice || 0)}
                                            </div>
                                        )}
                                        {entry.quantity && entry.itemId && (
                                            <span className="text-sm font-semibold text-green-600">
                                                {formatCurrency(parseFloat(entry.quantity) * (getItemDetails(entry.itemId)?.sellingPrice || 0))}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="col-span-1">
                                    <div className="flex justify-center items-end h-full">
                                        {salesEntries.length > 1 && (
                                            <button
                                                onClick={() => removeEntry(entry.id)}
                                                className="p-2 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Total and Submit */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                    <div className="flex items-center space-x-3">
                        <IndianRupee className="h-6 w-6 text-green-600" />
                        <div>
                            <p className="text-sm text-gray-600">Total Amount</p>
                            <p className="text-2xl font-bold text-green-600">{formatCurrency(calculateTotal())}</p>
                        </div>
                    </div>

                    <button
                        onClick={handleSubmit}
                        disabled={isLoading || calculateTotal() === 0}
                        className="w-full sm:w-auto flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                        {isLoading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                <span>Saving...</span>
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4" />
                                <span>Save Bulk Sales</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BulkSalesEntry;